import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Receipt, DollarSign, Calendar, Package,
  Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Un error se muestra como error, no como lista vacia (revision del 26-oct, E.6).
  const [loadError, setLoadError] = useState('');
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(`${API}/transactions`, {
          headers: getAuthHeaders()
        });
        setTransactions(response.data);
        setLoadError('');
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
        setLoadError(apiErrorMessage(error.response?.data, 'Could not load your transactions. Please try again.'));
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [getAuthHeaders]);

  const getTotalSpend = () => {
    return transactions.reduce((total, t) => total + t.total_amount, 0);
  };

  const getServiceLabel = (service) => {
    const labels = {
      prefraud: 'Prefraud Protection',
      verify_transaction: 'Transaction Verification',
      basic_coverage: 'Basic Coverage',
      full_coverage: 'Full Coverage',
      selleverify: 'SellVerify ID',
      ledger_fingerprint: 'Ledger Fingerprint',
      item_scoring: 'Item Scoring',
      // Compras de etiqueta en Rate Shop (charge_lines de la etiqueta).
      shipping: 'Shipping Label',
      admin_fee: 'Admin Fee',
      coverage: 'Coverage'
    };
    return labels[service] || service;
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="transactions-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Transactions</h1>
        <p className="text-slate-500 mt-1">View your billing and transaction history</p>
      </div>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Spend</p>
                <p className="text-2xl md:text-3xl font-semibold tabular-nums">
                  ${getTotalSpend().toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Transactions</p>
                <p className="text-2xl md:text-3xl font-semibold tabular-nums">
                  {transactions.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Avg. Transaction</p>
                <p className="text-2xl md:text-3xl font-semibold tabular-nums">
                  ${transactions.length > 0 ? (getTotalSpend() / transactions.length).toFixed(2) : '0.00'}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : loadError ? (
        <p className="text-sm text-red-600 py-12 text-center" data-testid="transactions-error">{loadError}</p>
      ) : transactions.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Services</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                      <td>
                        <code className="font-mono text-sm">{tx.id.slice(0, 8)}...</code>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {tx.services.map((service) => (
                            <span 
                              key={service}
                              className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full"
                            >
                              {getServiceLabel(service)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="tabular-nums font-medium">
                        ${tx.total_amount.toFixed(2)}
                      </td>
                      <td>
                        <span className={`status-badge status-${tx.status}`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="text-slate-500">
                        {new Date(tx.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium mb-2">No Transactions Yet</h3>
            <p className="text-slate-500 max-w-sm mx-auto">
              Your transaction history will appear here after you create shipments and run verifications.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TransactionsPage;
